// src/components/SearchForm.js
import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { alpha } from '@mui/material/styles';

// Sample client list (only used for Mintsoft orders)
const clients = [
  { id: 34, name: 'Au Vodka' },
  { id: 33, name: 'Branston' },
  { id: 40, name: 'Black Mirror' },
  { id: 20, name: 'Chapel Down' },
  { id: 13, name: 'Domaine Watson' },
  { id: 11, name: 'Echo Three' },
  { id: 35, name: 'Fox & Vamp' },
  { id: 36, name: 'Funky Pigeon' },
  { id: 8, name: 'Glenmorangie' },
  //{ id: 22, name: 'Glenfiddich' },
  { id: 29, name: 'Food Hugggers UK' },
  { id: 27, name: 'Lady A' },
  { id: 43, name: 'Maree Wines' },
  //{ id: 28, name: 'NOOH by La Coste' },
  { id: 14, name: 'Renais' },
  { id: 10, name: 'Schwartz' },
  //{ id: 21, name: 'Who Gives a Crap' },
  { id: 37, name: 'Wobbly Life' },
  //{ id: 38, name: 'Wolfies' },
  { id: 44, name: 'Krude Oil' },
  { id: 45, name: 'The Naked Doula' },
  { id: 48, name: 'No 98 Beauty' },
  { id: 49, name: 'Edmunds Cocktails' },
].sort((a, b) => a.name.localeCompare(b.name));

export default function SearchForm({
  searchParams,
  setSearchParams,
  printers,
  printerProxyUnavailable = false,
  selectedPrinter,
  setSelectedPrinter,
  onSearch,
  loading,
  inputRef,
  isDispatch,
  orderSource
}) {
  const [warnNonDigit, setWarnNonDigit] = useState(false);
  const isOrderNumberMode = searchParams.searchType === 'orderNumber';
  const stationTitle = isDispatch
    ? orderSource === 'kornitx'
      ? 'KornitX Dispatch Station'
      : 'Mintsoft Dispatch Station'
    : 'Reprint Shipping Label & Additional Documents';
  const stationDescription = isDispatch
    ? orderSource === 'kornitx'
      ? 'Search for a KornitX order by order number.'
      : 'Scan the Mintsoft order ID, confirm the order, then dispatch and print labels.'
    : 'Search for a dispatched order and reprint shipping labels or additional documents.';
  const searchInputLabel = isOrderNumberMode
    ? 'Order Number / External Ref'
    : 'Scan Order ID Barcode';
  const searchInputHelper = isOrderNumberMode
    ? 'Use when searching by customer-facing order number.'
    : 'Scanner input is focused automatically. Press Enter to search.';
  const actionLabel = isDispatch ? 'Search Order' : 'Search & Reprint';

  // Focus the input when the form mounts or source changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [orderSource, inputRef]);

  const handleOrderNumberChange = e => {
    const val = e.target.value;
    // In OrderId mode, if any non-digit appears...
    if (searchParams.searchType === 'orderId' && /[^0-9]/.test(val)) {
      // switch to orderNumber mode and show warning
      setSearchParams(prev => ({
        ...prev,
        searchType: 'orderNumber',
        orderNumber: val
      }));
      setWarnNonDigit(true);
    } else {
      // normal update
      setSearchParams(prev => ({
        ...prev,
        orderNumber: val
      }));
    }
  };

  return (
    <Paper
      sx={{
        p: { xs: 1.25, md: 1.5 },
        mb: 1.5,
        borderRadius: 3,
        border: '1px solid',
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.13),
        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.075)',
        width: '100%',
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.25,
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={900} sx={{ lineHeight: 1.15, color: 'text.primary' }}>
            {stationTitle}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {stationDescription}
          </Typography>
        </Box>
        <Box
          sx={{
            px: 1,
            py: 0.55,
            borderRadius: 99,
            bgcolor: (theme) => alpha(theme.palette.success.main, 0.08),
            color: 'success.dark',
            fontSize: 12,
            fontWeight: 800,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          Ready to scan
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: '172px minmax(420px, 1fr) 168px',
          },
          gap: 1,
          alignItems: 'stretch',
        }}
      >
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
            bgcolor: '#f7f9fc',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.75, fontSize: 11, fontWeight: 900, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.55 }}>
            Search By
          </Typography>
          <ToggleButtonGroup
            value={searchParams.searchType}
            exclusive
            onChange={(e, value) => {
              if (!value) return;
              setSearchParams(prev => ({
                ...prev,
                searchType: value
              }));
            }}
            orientation="vertical"
            fullWidth
            size="small"
            sx={{
              gap: 0.5,
              '& .MuiToggleButtonGroup-grouped': {
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: '10px !important',
                m: 0,
              },
              '& .MuiToggleButton-root': {
                justifyContent: 'flex-start',
                px: 1,
                py: 0.65,
                textTransform: 'none',
                fontSize: 12.5,
                fontWeight: 800,
                color: 'text.secondary',
                bgcolor: '#fff',
              },
            }}
          >
            <ToggleButton
              value="orderNumber"
            >
              Order Number
            </ToggleButton>
            {orderSource !== 'kornitx' && (
              <ToggleButton
                value="orderId"
              >
                Order ID
              </ToggleButton>
            )}
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            border: '1px solid',
            borderColor: (theme) => alpha(theme.palette.primary.main, 0.32),
            bgcolor: '#ffffff',
            boxShadow: 'inset 0 0 0 1px rgba(25, 118, 210, 0.035)',
          }}
        >
          <Typography variant="caption" sx={{ display: 'block', mb: 0.65, fontWeight: 900, color: 'primary.main', textTransform: 'uppercase', letterSpacing: 0.55 }}>
            Scan / Search
          </Typography>
          <TextField
            size="medium"
            label={searchInputLabel}
            fullWidth
            value={searchParams.orderNumber}
            onChange={handleOrderNumberChange}
            onKeyDown={e => {
              if (e.key === 'Enter') onSearch();
            }}
            inputRef={inputRef}
            autoComplete="off"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <QrCodeScannerIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
            }}
            inputProps={{
              autoComplete: 'off',
              ...(isOrderNumberMode
                ? {}
                : { inputMode: 'numeric', pattern: '[0-9]*' })
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: '#fff',
                fontSize: 18,
                fontWeight: 800,
              },
              '& .MuiInputBase-input': {
                py: 1.15,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.6 }}>
            {searchInputHelper}
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Button
            variant="contained"
            size="large"
            onClick={onSearch}
            disabled={loading}
            sx={{
              height: 48,
              fontWeight: 900,
              textTransform: 'none',
              borderRadius: 1.75,
              whiteSpace: 'nowrap',
              fontSize: 13,
            }}
          >
            {loading ? (
              <CircularProgress size={22} />
            ) : (
              actionLabel
            )}
          </Button>
          <Button
            variant="outlined"
            size="medium"
            onClick={() =>
              setSearchParams(prev => ({ ...prev, orderNumber: '' }))
            }
            disabled={loading}
            startIcon={<ClearIcon />}
            sx={{ height: 38, fontWeight: 800, textTransform: 'none', borderRadius: 1.75 }}
          >
            Clear
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: isOrderNumberMode && orderSource !== 'kornitx' ? 'minmax(220px, 280px) minmax(260px, 360px)' : 'minmax(260px, 360px)',
          },
          gap: 1.25,
          mt: 1.1,
          maxWidth: isOrderNumberMode && orderSource !== 'kornitx' ? 660 : 360,
        }}
      >
        {orderSource !== 'kornitx' &&
          isOrderNumberMode && (
            <FormControl size="small" fullWidth>
              <InputLabel id="client-select-label">
                Client
              </InputLabel>
              <Select
                labelId="client-select-label"
                value={searchParams.selectedClientId}
                label="Client"
                onChange={e =>
                  setSearchParams(prev => ({
                    ...prev,
                    selectedClientId: e.target.value
                  }))
                }
              >
                {clients.map(c => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        <FormControl
          size="small"
          fullWidth
          disabled={printerProxyUnavailable}
        >
          <InputLabel id="printer-select-label">
            Label Printer
          </InputLabel>
          <Select
            labelId="printer-select-label"
            value={selectedPrinter}
            label="Label Printer"
            onChange={e => setSelectedPrinter(e.target.value)}
          >
            {printers.map((p, i) => (
              <MenuItem key={i} value={p}>
                {p}
              </MenuItem>
            ))}
          </Select>
          {printerProxyUnavailable ? (
            <FormHelperText>
              Local print proxy not detected. Browser print fallback will still work.
            </FormHelperText>
          ) : null}
        </FormControl>
      </Box>

      {warnNonDigit && (
        <Alert
          severity="warning"
          onClose={() => setWarnNonDigit(false)}
          sx={{ mt: 1.25 }}
        >
          You selected “Order Id,” but entered non-numeric characters. We’ve switched to “Order Number” mode.
        </Alert>
      )}
    </Paper>
  );
}
